import { useQuery, useQueryClient } from '@tanstack/react-query';

import { DataStorage, StorageData } from '@suntex.core/storage';
import { KnownKeys } from '@suntex.core/types';

import { asyncPromiseCache } from './async-promise-cache';
import { useSessionData } from './useSessionData';

export interface SessionDataResult<T> {
  readonly data: T | null;
  readonly isLoading: boolean;
  readonly error: Error | null;
  reload: () => void;
}

/**
 * @param key must be static (no change detection)
 * @param asyncGetter must be static (no change detection)
 * @returns
 */
export function useSessionDataLoader<
  T extends StorageData,
  K extends KnownKeys<T> & string,
>(key: K, asyncGetter: () => Promise<T[K]>): SessionDataResult<T[K]> {
  const sessionDataStorage = useSessionData<T>();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['session', key],
    queryFn: () => getAsyncSessionData(sessionDataStorage, key, asyncGetter),
    staleTime: Infinity,
  });

  function reload() {
    void queryClient.invalidateQueries({ queryKey: ['session', key] });
  }

  return { data: data ?? null, isLoading, error, reload };
}

/**
 * Load data from session storage or fetch it asynchronously.
 *
 * This function implements two levels of caching:
 * 1. Session storage cache (persistent across component remounts)
 * 2. In-flight promise cache (deduplicates concurrent requests)
 *
 * When multiple components request the same key simultaneously,
 * only one fetch operation occurs and all callers share the result.
 *
 * @param sessionDataStorage - The session storage instance
 * @param key - Unique key for this data (must be consistent)
 * @param asyncGetter - Function to fetch data if not cached
 * @returns Promise resolving to the data
 */
async function getAsyncSessionData<
  T extends StorageData,
  K extends KnownKeys<T> & string,
>(
  sessionDataStorage: DataStorage<T>,
  key: K,
  asyncGetter: () => Promise<T[K]>,
): Promise<T[K]> {
  // First: check session storage (synchronous cache)
  const cachedItem = sessionDataStorage.get(key);
  if (cachedItem !== undefined) return cachedItem;

  // Second: check/use promise cache (deduplication)
  return asyncPromiseCache.getOrCreate(key, async () => {
    // Triple-check session storage to handle race conditions
    // (another component's promise may have just completed)
    const recheck = sessionDataStorage.get(key);
    if (recheck !== undefined) return recheck;

    // Fetch the data
    const data = await asyncGetter();

    // Cache in session storage
    sessionDataStorage.set(key, data);

    return data;
  });
}
